/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useTheme, Theme } from '@mui/material';
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';
import React from 'react';

import { generateRouteUrl } from 'src/boot/routes';
import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';

export const Link = ({
  'data-testid': dataTestid,
  children,
  color = 'inherit',
  onClick,
  onKeyPress,
  type,
  href,
  variant = 'inherit',
  external = false,
  route,
  params,
  StartIcon,
  EndIcon,
  component = onClick ? 'button' : 'a',
}: React.PropsWithChildren<
  {
    color?: MuiLinkProps['color'];

    /**
     * The string value of the route name to navigate to. This is used to generate the mobx-state-router URL.
     */
    route?: string;

    /**
     * The parameters to pass to the route. This is used to generate the mobx-state-router URL.
     */
    params?: Record<string, unknown>;

    'data-testid'?: string;
    StartIcon?: React.ReactNode;
    EndIcon?: React.ReactNode;
    /**
     * Whether the link should be treated as an external link. If true, it will open in a new tab
     * and an icon will be added indicating that it is an external link. If an `endIcon` is
     * provided, it will be used instead of the default external link icon.
     */
    external?: boolean;
  } & Pick<MuiLinkProps, 'href' | 'type' | 'variant' | 'onClick' | 'onKeyPress' | 'component'>
>) => {
  const theme = useTheme();
  const fontColor = getColor(color, theme);

  const [endIcon, setEndIcon] = React.useState<React.ReactNode | null>(EndIcon || null);
  if (external && !EndIcon && endIcon === null) {
    setEndIcon(
      <ExitToAppIcon
        css={css`
          font-size: 1.4em;
          padding-bottom: 1px;
        `}
      />,
    );
  } else if (!external && !EndIcon && endIcon !== null) {
    setEndIcon(null);
  }

  let hrefValue = href;
  if (!href && route) {
    try {
      hrefValue = generateRouteUrl(route, params);
    } catch (e) {
      console.error('Error generating route URL:', e);
    }
  }

  const inheritColor = color === 'inherit';

  return (
    <MuiLink
      component={component}
      css={css`
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        ${!inheritColor && `color: ${fontColor};`}
        text-decoration: underline;
        transition: color 0.1s ease-in-out;

        &:focus {
          outline: 2px solid ${Colors.BlueSpruce};
        }

        ${!inheritColor &&
        css`
          &:hover {
            color: color-mix(in hsl, ${fontColor}, ${Colors.Black} 20%);
          }

          &:active {
            color: color-mix(in hsl, ${fontColor}, ${Colors.Black} 40%);
          }
        `}
      `}
      data-testid={dataTestid || 'nds-component-link'}
      onClick={onClick}
      onKeyPress={onKeyPress}
      variant={variant}
      type={type}
      color={color}
      href={hrefValue}
      target={external ? '_blank' : undefined}
    >
      {React.isValidElement(StartIcon) ? React.cloneElement(StartIcon) : null}
      <span
        css={css`
          display: inline-block;
          margin-left: ${StartIcon ? Spacing.Unit1 : '0'}px;
          margin-right: ${endIcon ? Spacing.Unit1 : '0'}px;
        `}
      >
        {children}
      </span>
      {React.isValidElement(endIcon) ? React.cloneElement(endIcon) : null}
    </MuiLink>
  );
};

const getColor = (color: MuiLinkProps['color'], theme: Theme) =>
  color === 'inherit' || !color ? 'inherit' : theme.palette[color].main;
